import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Form, Select, Button, Spin, message } from 'antd'
import {
    apiAddTag,
    apiGetTagList,
} from '../../../../../redux/testCases/testCase.service'

// ---- Replace these with your real endpoints ----
async function fetchOptionsAPI(query = '') {
    // Example: GET /api/tags?search=query -> [{ id, name }]
    // const res = await fetch(`/api/tags?search=${encodeURIComponent(query)}`)
    // if (!res.ok) throw new Error('Failed to fetch options')
    // const data = await res.json()

    const params = {
        skip: 0,
        limit: 20,
        ...(query ? { search: { name: query } } : {}),
        // sort: 'createdAt',
        // sortType: 'asc',
    }
    const res = await apiGetTagList(params)
    const list = res?.data?.data ?? res?.data ?? []
    // Normalize to { value, label }
    return (list || []).map((x) => ({ value: x._id, label: x.name }))
}

async function addOptionAPI(label) {
    // Example: POST /api/tags { name: label } -> { id, name }
    const created = await apiAddTag({ name: label })
    const respData = created?.data?.data || {}
    // if (!res.ok) throw new Error('Failed to add option')
    // const x = await res.json()
    return { value: respData?._id ?? '', label: respData?.name ?? '' }
}
// ------------------------------------------------

// Small debounce helper (no external deps)
function useDebouncedCallback(cb, delay = 400) {
    const t = useRef()
    return (...args) => {
        clearTimeout(t.current)
        t.current = setTimeout(() => cb(...args), delay)
    }
}

function AsyncTagField({
    name = 'tags',
    label = 'Tags',
    placeholder = 'Search or add tags…',
    rules = [{ required: true, message: 'Please choose at least one item' }],
    initialSearch = '',
    form,
    creatable = true,
}) {
    // const [form] = Form.useForm() // useForm inside if it's a standalone form. If you already have a form, pass it down instead.
    const [options, setOptions] = useState([])
    const [fetching, setFetching] = useState(false)
    const [search, setSearch] = useState(initialSearch)
    const [adding, setAdding] = useState(false)
    const [open, setOpen] = useState(false)

    const loadOptions = async (q) => {
        setFetching(true)
        try {
            const opts = await fetchOptionsAPI(q)
            setOptions(opts)
        } catch (e) {
            message.error(e.message || 'Failed to load options')
        } finally {
            setFetching(false)
        }
    }

    // Initial load
    useEffect(() => {
        loadOptions('')
    }, [])

    const debouncedSearch = useDebouncedCallback((q) => loadOptions(q), 350)

    const onSearch = (value) => {
        setSearch(value)
        debouncedSearch(value)
    }

    // Whether current search text exists in options (case-insensitive)
    const hasExactMatch = useMemo(() => {
        const s = (search || '').trim().toLowerCase()
        if (!s) return true // don’t show Add button on empty search
        return options.some((o) => String(o.label).toLowerCase() === s)
    }, [search, options])

    const refreshAllOptionsWith = async (
        extra /* {value,label} | undefined */
    ) => {
        setFetching(true)
        try {
            const full = await fetchOptionsAPI('') // <- unfiltered
            const merged = extra
                ? [extra, ...full.filter((o) => o.value !== extra.value)]
                : full
            setOptions(merged)
        } catch (e) {
            message.error(e.message || 'Failed to refresh options')
        } finally {
            setFetching(false)
        }
    }

    const onAdd = async () => {
        const labelToAdd = (search || '').trim()
        if (!labelToAdd || hasExactMatch) return

        setAdding(true)
        try {
            // ✅ use normalized object directly
            const created = await addOptionAPI(labelToAdd) // { value, label }

            // ✅ ensure the option with correct value+label is present immediately
            setOptions((prev) => {
                if (prev.some((p) => p.value === created.value)) return prev
                return [created, ...prev] // prepend so it's visible with current search
            })

            // ✅ append the value to the current selection
            const current = form.getFieldValue(name) || []
            form.setFieldsValue({ [name]: [...current, created.value] })

            message.success(`Added "${created.label}"`)

            // Optional: clear search and keep dropdown open
            setSearch('')
            await refreshAllOptionsWith(created)
            // No need to await loadOptions('') immediately; the option is already in state.
            // You can refresh silently in the background if you want:
            // void loadOptions('');

            setOpen(true)
        } catch (e) {
            message.error(e.message || 'Failed to add')
        } finally {
            setAdding(false)
        }
    }
    return (
        <Form.Item name={name} label={label} rules={rules}>
            <Select
                className="test-rail-select"
                mode="multiple"
                showSearch
                labelInValue
                allowClear
                placeholder={placeholder}
                filterOption={false}
                onSearch={onSearch}
                // ✅ control the input text so clearing state clears the UI
                searchValue={search}
                options={options}
                loading={fetching}
                notFoundContent={
                    !fetching &&
                    creatable && (
                        <div style={{ textAlign: 'center' }}>
                            Not found for “{search}”
                        </div>
                    )
                }
                dropdownRender={(menu) => (
                    <>
                        {fetching && ( // 👈 top inline loader
                            <div
                                style={{
                                    padding: 8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                }}
                            >
                                <Spin size="small" />
                                <span>Loading…</span>
                            </div>
                        )}
                        {menu}
                        {!hasExactMatch &&
                            creatable &&
                            search?.trim()?.length > 0 && (
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: 8,
                                        padding: 8,
                                    }}
                                >
                                    <Button
                                        type="default"
                                        block
                                        loading={adding}
                                        onMouseDown={(e) => e.preventDefault()} // ✅ prevents close before click
                                        onClick={onAdd}
                                    >
                                        Add “{search}”
                                    </Button>
                                </div>
                            )}
                    </>
                )}
                autoClearSearchValue={true}
                open={open}
                onDropdownVisibleChange={(v) => {
                    if (!v && (!hasExactMatch || search)) setOpen(true)
                    else setOpen(v)
                }}
            />
        </Form.Item>
    )
}

export default AsyncTagField
